// ── Kanban board page ──

import { api } from '../api.js';
import { checkAuth, getUser } from '../auth.js';
import { showToast } from '../ui.js';
import { escapeHtml, renderAvatarHtml, renderAgentBadge, renderDuePill, canStartTask } from '../utils.js';
import { initLayout, getContentEl, updateLayoutContext } from '../layout.js';
import { openTaskDetail, closeTaskDetail } from '../components/task-detail.js';
import { showAssigneePicker, removeAssigneePicker } from '../components/assignee-picker.js';
import { renderQuickCreate, bindQuickCreate, removeQuickCreate } from '../components/quick-create.js';
import { loadStatuses, statusLabel, roleStatus, invalidateStatuses, isClosedStatus, closedNames } from '../lib/statuses.js';
import { findInsertBefore, dropIndex } from '../lib/drag-order.js';

const DRAG_THRESHOLD = 5;
// Grab-to-pan / edge auto-scroll (TPT113).
const EDGE_ZONE = 64; // px from the board's own edge that arms auto-scroll
const EDGE_MAX_SPEED = 20; // px/frame at the very edge, ramped down across the zone
const INTERACTIVE_SEL = 'a, button, input, textarea, select, [contenteditable]';

let state = {
  projectId: null,
  tasks: [],
  members: [],
  statuses: [],
  drag: null,
  // Edit Board (C1183) — column-header drag reorder, threaded from board.js's toolbar
  // toggle. Card drag-and-drop (existing) stays live regardless of this flag.
  editBoardMode: false,
  onStatusesChanged: null,
  onDataChanged: null, // bubbles "server data changed" up to board.js (C1261) — see reloadTasks()
  colDrag: null,
  pan: null, // TPT113 grab-to-pan
};

function resetState() {
  state = {
    projectId: null,
    tasks: [],
    members: [],
    statuses: [],
    drag: null,
    editBoardMode: false,
    onStatusesChanged: null,
    onDataChanged: null,
    colDrag: null,
    pan: null,
  };
}

// Free board space = not a card, not a native control. Shared by grab-to-pan and the
// Edit Board column drag (TPT113) so the two gestures never disagree about what counts
// as "empty" — `.kanban-column-drag-handle` is excluded by callers individually, since
// it means different things in each mode (only a handle in normal mode; either handle
// or free column space starts a column drag in Edit Board mode).
function isFreeBoardSpace(target) {
  return !target.closest('.kanban-card') && !target.closest(INTERACTIVE_SEL);
}

// Bubble a data-changing event (drawer close, drag-save failure, quick-create) up to
// board.js's own reload rather than refetching into state.tasks here — state.tasks IS
// board.js's `filtered` array (applyFilters output), a throwaway once any filter is active
// (C1261). Falls back to a local refetch+splice only for the never-imported legacy
// kanbanPage() below, which owns state.tasks outright and passes no onDataChanged.
async function reloadTasks() {
  if (state.onDataChanged) {
    await state.onDataChanged();
    return;
  }
  try {
    const { tasks } = await api.get('/projects/' + state.projectId + '/tasks');
    state.tasks.splice(0, state.tasks.length, ...(tasks || []));
  } catch (_) {}
  renderKanban();
}

// Columns follow this project's Workflow tab order (display_order ASC) — one column
// per status, colored by that status's own `color` token (C1182). `loadStatuses()`
// already returns display_order-sorted rows (falling back to the legacy 5 on error), so
// this just maps them; no separate fetch/sort here. `id` carries through for the
// Edit Board (C1183) column-drag reorder payload (PUT /statuses/reorder wants status ids).
function getColumns() {
  return state.statuses.map(s => ({
    id: s.id,
    key: s.name,
    label: statusLabel(s.name),
    dot: s.color ? `var(--c-${s.color})` : 'var(--c-overlay1)',
  }));
}

// ── Rendering ──

function renderKanbanCard(task) {
  const catClass = task.category === 'CODING' ? 'badge-coding' : 'badge-human';
  const tags = (task.tags || []).slice(0, 3);
  const extraTags = (task.tags || []).length - 3;

  let html = `<div class="kanban-card" data-task-key="${escapeHtml(task.task_key)}">`;
  html += `<a class="badge kanban-card-key ${catClass} task-key-link" href="#/projects/${state.projectId}/tasks/${encodeURIComponent(task.task_key)}" draggable="false" title="Open task page">${escapeHtml(task.task_key)}</a>`;
  html += `<div class="kanban-card-title">${escapeHtml(task.title)}</div>`;
  html += renderDuePill(task, closedNames(state.statuses));
  const _bd = task.active_device || task.recent_device;
  // Role-based "not done yet" check (C1182, closed via complete OR canceled role as of C1187).
  if (_bd && !isClosedStatus(state.statuses, task.status)) {
    const _bdActive = !!task.active_device;
    html += `<span class="device-badge${_bdActive ? '' : ' device-badge--recent'}" title="${escapeHtml(_bdActive ? `Active on ${_bd.name}` : `Last worked on ${_bd.name}`)}">${escapeHtml(_bd.name)}</span>`;
  }
  const assigneeUser = task.assignee ? state.members.find(m => (m.user_id || m.id) === task.assignee) : null;
  html += `<div style="display:flex;align-items:flex-end;gap:0.5rem;">`;
  if (tags.length) {
    html += `<div class="kanban-card-tags" style="flex:1;min-width:0;">`;
    html += tags.map(t => `<span class="tag">${escapeHtml(t)}</span>`).join('');
    if (extraTags > 0) html += `<span class="text-faint" style="font-size:0.7rem"> +${extraTags}</span>`;
    html += `</div>`;
  } else {
    html += `<div style="flex:1"></div>`;
  }
  html += `<div class="card-avatar-wrap" data-task-key="${escapeHtml(task.task_key)}" data-action="assign">`;
  html += renderAvatarHtml(assigneeUser, 24);
  html += renderAgentBadge(task, state.members, { overlap: true });
  html += `</div></div>`;
  html += `</div>`;
  return html;
}

function renderColumn(col, tasks) {
  let html = `<div class="kanban-column" data-status-id="${col.id}">`;
  html += `<div class="kanban-column-header${state.editBoardMode ? ' kanban-column-header--editable' : ''}"${state.editBoardMode ? ' title="Drag to reorder"' : ''}>`;
  if (state.editBoardMode) html += `<span class="kanban-column-drag-handle">&#10495;</span>`;
  html += `<span class="kanban-column-dot" style="background:${col.dot}"></span>`;
  html += `<span>${col.label}</span>`;
  html += `<span class="kanban-column-count">${tasks.length}</span>`;
  html += `</div>`;
  html += `<div class="kanban-column-body" data-status="${col.key}">`;
  html += tasks.map(renderKanbanCard).join('');
  html += renderQuickCreate('kanban-' + col.key);
  html += `</div></div>`;
  return html;
}

function renderKanban() {
  const board = document.getElementById('kanban-board');
  if (!board) return;

  const columns = getColumns();
  const grouped = new Map();
  for (const col of columns) grouped.set(col.key, []);
  for (const t of state.tasks) {
    const arr = grouped.get(t.status);
    if (arr) arr.push(t);
  }

  // Always render every status column — even when the current sprint selection
  // matches zero tasks — so every status stays a valid drop target.
  // (TPT112) Preserve horizontal scroll position across re-renders — innerHTML
  // resets scrollLeft to 0, which would snap the user back to the leftmost column
  // on every status drag, quick-create, and reload once horizontal scroll is the
  // primary way to reach far columns.
  const scrollLeft = board.scrollLeft;
  board.innerHTML = columns.map(col => renderColumn(col, grouped.get(col.key) || [])).join('');
  board.scrollLeft = scrollLeft;
  // (TPT112) CSS hook — styles.css uses [data-columns="1|2|3"] to widen columns to
  // fill available width on a project with few statuses, instead of a dead gutter.
  board.dataset.columns = columns.length;
}

// ── Edge auto-scroll (TPT113) ──
// Shared by card drag and Edit Board column drag — a drag held near the board's left/right
// edge scrolls the board under the pointer so an offscreen column becomes a valid drop
// target without releasing. rAF-driven rather than mousemove-driven: a pointer held still
// inside the edge zone must keep scrolling. Because the board's DOM moves under a
// stationary cursor, each tick re-invokes the caller's own drop-target resolution at the
// last known pointer position — otherwise the placeholder (and the drop target read on
// mouseup) goes stale mid-scroll.

let edgeScrollRaf = null;
let edgeScrollVel = 0;
let edgeScrollBoard = null;
let edgeScrollOnTick = null;

function edgeScrollTick() {
  if (!edgeScrollBoard || edgeScrollVel === 0) {
    edgeScrollRaf = null;
    return;
  }
  edgeScrollBoard.scrollLeft += edgeScrollVel; // scrollLeft self-clamps at both ends
  if (edgeScrollOnTick) edgeScrollOnTick();
  edgeScrollRaf = requestAnimationFrame(edgeScrollTick);
}

// Call on every drag mousemove with the pointer's clientX and a callback that re-resolves
// the caller's drop target at the drag's last known pointer position.
function updateEdgeScroll(board, clientX, onTick) {
  edgeScrollBoard = board;
  edgeScrollOnTick = onTick;
  const rect = board.getBoundingClientRect();
  let vel = 0;
  if (clientX < rect.left + EDGE_ZONE) {
    const depth = Math.max(0, rect.left + EDGE_ZONE - clientX);
    vel = -EDGE_MAX_SPEED * Math.min(1, depth / EDGE_ZONE);
  } else if (clientX > rect.right - EDGE_ZONE) {
    const depth = Math.max(0, clientX - (rect.right - EDGE_ZONE));
    vel = EDGE_MAX_SPEED * Math.min(1, depth / EDGE_ZONE);
  }
  edgeScrollVel = vel;
  if (vel !== 0 && edgeScrollRaf == null) edgeScrollRaf = requestAnimationFrame(edgeScrollTick);
}

function stopEdgeScroll() {
  if (edgeScrollRaf != null) cancelAnimationFrame(edgeScrollRaf);
  edgeScrollRaf = null;
  edgeScrollVel = 0;
  edgeScrollBoard = null;
  edgeScrollOnTick = null;
}

// ── Drag and drop ──

let onMouseDown, onMouseMove, onMouseUp;

// Resolve and highlight the column-body drop target + placeholder position for the
// active card drag at the given viewport point. Extracted from onMouseMove (TPT113) so
// the edge-auto-scroll rAF tick can re-run it at the drag's last known pointer position
// while the board scrolls under a stationary cursor.
function updateCardDropTarget(clientX, clientY) {
  const d = state.drag;
  if (!d || !d.placeholder) return;

  const target = document.elementFromPoint(clientX, clientY);
  if (!target) return;
  const colBody = target.closest('.kanban-column-body');

  // Clear all drag-over highlights
  document.querySelectorAll('.kanban-column-body.drag-over').forEach(el => el.classList.remove('drag-over'));

  if (!colBody) return;
  colBody.classList.add('drag-over');

  // Find insertion point within column
  const cards = [...colBody.querySelectorAll('.kanban-card:not(.kanban-card-hidden)')];
  const insertBefore = findInsertBefore(cards, clientX, clientY, 'y');

  // Move placeholder
  if (insertBefore) {
    colBody.insertBefore(d.placeholder, insertBefore);
  } else {
    colBody.appendChild(d.placeholder);
  }
}

function bindDragAndDrop() {
  onMouseDown = (e) => {
    const card = e.target.closest('.kanban-card');
    if (!card) return;
    if (e.target.closest('select, button, a, input, textarea, [contenteditable]')) return;

    // Avatar click → assignee picker, no drag
    const avatarWrap = e.target.closest('.card-avatar-wrap');
    if (avatarWrap) {
      e.preventDefault();
      const taskKey = avatarWrap.dataset.taskKey;
      showAssigneePicker(avatarWrap, {
        members: state.members,
        currentAssignee: state.tasks.find(t => t.task_key === taskKey)?.assignee,
        onSelect: (userId) => handleAssign(taskKey, userId),
      });
      return;
    }

    const rect = card.getBoundingClientRect();
    state.drag = {
      taskKey: card.dataset.taskKey,
      el: card,
      clone: null,
      placeholder: null,
      startX: e.clientX,
      startY: e.clientY,
      offsetX: e.clientX - rect.left,
      offsetY: e.clientY - rect.top,
      moved: false,
    };
    e.preventDefault();
  };

  onMouseMove = (e) => {
    const d = state.drag;
    if (!d) return;

    const dx = e.clientX - d.startX;
    const dy = e.clientY - d.startY;

    // Wait for threshold before starting visual drag
    if (!d.moved) {
      if (Math.sqrt(dx * dx + dy * dy) < DRAG_THRESHOLD) return;
      d.moved = true;

      // Create floating clone
      d.clone = d.el.cloneNode(true);
      d.clone.classList.add('kanban-card-dragging');
      d.clone.style.width = d.el.offsetWidth + 'px';
      document.body.appendChild(d.clone);

      // Create placeholder
      d.placeholder = document.createElement('div');
      d.placeholder.className = 'kanban-card-placeholder';
      d.placeholder.style.height = d.el.offsetHeight + 'px';
      d.el.parentNode.insertBefore(d.placeholder, d.el);

      // Hide original
      d.el.classList.add('kanban-card-hidden');
    }

    // Position clone
    d.clone.style.left = (e.clientX - d.offsetX) + 'px';
    d.clone.style.top = (e.clientY - d.offsetY) + 'px';

    d.lastX = e.clientX;
    d.lastY = e.clientY;
    updateCardDropTarget(e.clientX, e.clientY);

    // TPT113 — auto-scroll the board when the dragged clone nears its left/right edge.
    // Re-resolves the drop target on every scroll tick since the DOM moves under a
    // pointer that may not itself be moving.
    const board = document.getElementById('kanban-board');
    if (board) updateEdgeScroll(board, e.clientX, () => updateCardDropTarget(d.lastX, d.lastY));
  };

  onMouseUp = async (e) => {
    const d = state.drag;
    if (!d) return;
    stopEdgeScroll();

    // Clear highlights
    document.querySelectorAll('.kanban-column-body.drag-over').forEach(el => el.classList.remove('drag-over'));

    if (!d.moved) {
      // Was a click, not a drag — open detail panel
      const clickedKey = d.taskKey;
      state.drag = null;
      openTaskDetail(state.projectId, clickedKey, {
        allTasks: state.tasks,
        onClose: reloadTasks,
      });
      return;
    }

    // Determine target status
    const colBody = d.placeholder.closest('.kanban-column-body');
    const targetStatus = colBody ? colBody.dataset.status : null;

    // Cleanup DOM
    if (d.clone) d.clone.remove();
    if (d.placeholder) d.placeholder.remove();
    d.el.classList.remove('kanban-card-hidden');

    const task = state.tasks.find(t => t.task_key === d.taskKey);
    state.drag = null;

    if (!task || !targetStatus || task.status === targetStatus) return;

    // Block moving another member's task onto the in-progress-role status
    const inProgressName = roleStatus(state.statuses, 'in_progress')?.name;
    if (targetStatus === inProgressName && !canStartTask(task, getUser())) {
      showToast('Cannot start a task assigned to another member', 'error');
      return;
    }

    // Optimistic update
    task.status = targetStatus;
    renderKanban();

    try {
      await api.patch('/projects/' + state.projectId + '/tasks/' + task.task_key, { status: targetStatus });
    } catch (err) {
      showToast('Failed to update status', 'error');
      // Server is the truth now — re-derive everything through the owner rather than
      // splicing an unfiltered refetch into this (possibly filtered) array (C1261).
      await reloadTasks();
    }
  };

  document.addEventListener('mousedown', onMouseDown);
  document.addEventListener('mousemove', onMouseMove);
  document.addEventListener('mouseup', onMouseUp);
}

async function handleAssign(taskKey, userId) {
  const task = state.tasks.find(t => t.task_key === taskKey);
  if (!task) return;
  const old = task.assignee;
  task.assignee = userId;
  renderKanban();
  try {
    await api.patch('/projects/' + state.projectId + '/tasks/' + taskKey, { assignee: userId });
  } catch (err) {
    task.assignee = old;
    renderKanban();
    showToast('Failed to assign', 'error');
  }
}

function cleanupDrag() {
  stopEdgeScroll();
  if (state.drag) {
    if (state.drag.clone) state.drag.clone.remove();
    if (state.drag.placeholder) state.drag.placeholder.remove();
    if (state.drag.el) state.drag.el.classList.remove('kanban-card-hidden');
    state.drag = null;
  }
  document.querySelectorAll('.kanban-column-body.drag-over').forEach(el => el.classList.remove('drag-over'));
  if (onMouseDown) document.removeEventListener('mousedown', onMouseDown);
  if (onMouseMove) document.removeEventListener('mousemove', onMouseMove);
  if (onMouseUp) document.removeEventListener('mouseup', onMouseUp);
}

// ── Column-header drag reorder (Edit Board, C1183) ──
// Same house pointer-drag idiom as bindDragAndDrop() above and settings.js's Workflow
// tab row-drag (C1182) — clone + placeholder + 5px threshold, no HTML5 DnD. Horizontal
// instead of vertical: reorders `.kanban-column` siblings left-to-right instead of
// `.kanban-card`/`.wf-row` top-to-bottom. Listeners are only attached while
// `state.editBoardMode` is true (bindColumnDrag() call is itself conditional), so
// dragging a column header does nothing outside Edit Board mode.

let onColMouseDown, onColMouseMove, onColMouseUp;

// Resolve the column insertion point for the active column drag at the given viewport
// point. Extracted from onColMouseMove (TPT113) so the edge-auto-scroll rAF tick can
// re-run it while the board scrolls under a stationary cursor.
function updateColumnDropTarget(clientX, clientY) {
  const d = state.colDrag;
  if (!d || !d.placeholder) return;
  const board = document.getElementById('kanban-board');
  if (!board) return;
  const cols = [...board.querySelectorAll('.kanban-column:not(.kanban-column-hidden)')];
  const insertBefore = findInsertBefore(cols, clientX, clientY, 'x');
  if (insertBefore) board.insertBefore(d.placeholder, insertBefore);
  else board.appendChild(d.placeholder);
}

function bindColumnDrag() {
  onColMouseDown = (e) => {
    // TPT113 — a column drag now starts from the header handle OR any free space inside
    // the column (header text/dot/count, column body gaps), not just the drag handle.
    // Only reachable while state.editBoardMode is true — bindColumnDrag() is itself only
    // ever called in that mode, so this can't hijack a normal-mode click.
    const col = e.target.closest('.kanban-column');
    if (!col) return;
    if (!e.target.closest('.kanban-column-drag-handle') && !isFreeBoardSpace(e.target)) return;
    const rect = col.getBoundingClientRect();
    state.colDrag = {
      statusId: col.dataset.statusId,
      el: col,
      clone: null,
      placeholder: null,
      startX: e.clientX,
      startY: e.clientY,
      offsetX: e.clientX - rect.left,
      offsetY: e.clientY - rect.top,
      moved: false,
    };
    e.preventDefault();
  };

  onColMouseMove = (e) => {
    const d = state.colDrag;
    if (!d) return;

    const dx = e.clientX - d.startX;
    const dy = e.clientY - d.startY;

    if (!d.moved) {
      if (Math.sqrt(dx * dx + dy * dy) < DRAG_THRESHOLD) return;
      d.moved = true;

      d.clone = d.el.cloneNode(true);
      d.clone.classList.add('kanban-column-dragging');
      d.clone.style.width = d.el.offsetWidth + 'px';
      d.clone.style.height = d.el.offsetHeight + 'px';
      document.body.appendChild(d.clone);

      d.placeholder = document.createElement('div');
      d.placeholder.className = 'kanban-column-placeholder';
      d.placeholder.style.width = d.el.offsetWidth + 'px';
      d.placeholder.style.height = d.el.offsetHeight + 'px';
      d.el.parentNode.insertBefore(d.placeholder, d.el);

      d.el.classList.add('kanban-column-hidden');
    }

    d.clone.style.left = (e.clientX - d.offsetX) + 'px';
    d.clone.style.top = (e.clientY - d.offsetY) + 'px';

    d.lastX = e.clientX;
    d.lastY = e.clientY;
    updateColumnDropTarget(e.clientX, e.clientY);

    // TPT113 — auto-scroll the board when the dragged column clone nears its left/right edge.
    const board = document.getElementById('kanban-board');
    if (board) updateEdgeScroll(board, e.clientX, () => updateColumnDropTarget(d.lastX, d.lastY));
  };

  onColMouseUp = async (e) => {
    const d = state.colDrag;
    if (!d) return;
    stopEdgeScroll();
    state.colDrag = null;

    const board = document.getElementById('kanban-board');
    // Must run BEFORE the cleanup below removes 'kanban-column-hidden' from d.el — dropIndex()
    // counts elements NOT carrying that class, so a hidden-then-unhidden dragged original
    // would otherwise start counting itself (see lib/drag-order.js). `#kanban-board`'s only
    // children are `.kanban-column` elements (renderKanban() emits nothing else), but the
    // still-DOM-present hidden dragged column previously made a raw child-index overshoot by
    // one on every rightward reorder.
    const newIndex = (board && d.placeholder && board.contains(d.placeholder))
      ? dropIndex(board, d.placeholder, { selector: '.kanban-column', hiddenClass: 'kanban-column-hidden' })
      : -1;

    if (d.clone) d.clone.remove();
    if (d.placeholder) d.placeholder.remove();
    d.el.classList.remove('kanban-column-hidden');

    if (!d.moved || newIndex === -1) return;

    const ordered = [...state.statuses].sort((a, b) => a.display_order - b.display_order);
    const prevIds = ordered.map(s => s.id);
    const from = ordered.findIndex(s => String(s.id) === String(d.statusId));
    if (from === -1) return;
    const [moved] = ordered.splice(from, 1);
    ordered.splice(Math.min(newIndex, ordered.length), 0, moved);
    const ids = ordered.map(s => s.id);
    if (ids.every((sid, i) => sid === prevIds[i])) return; // dropped back in place

    // Optimistic reorder — render the new order immediately, persist after.
    ordered.forEach((s, i) => { s.display_order = i; });
    state.statuses = ordered;
    renderKanban();

    try {
      const res = await api.put(`/projects/${state.projectId}/statuses/reorder`, { ids });
      invalidateStatuses(state.projectId);
      state.statuses = res.statuses || state.statuses;
      if (state.onStatusesChanged) state.onStatusesChanged(state.statuses);
    } catch (err) {
      showToast(err.message || 'Failed to reorder columns', 'error');
      try {
        invalidateStatuses(state.projectId);
        state.statuses = await loadStatuses(state.projectId);
        if (state.onStatusesChanged) state.onStatusesChanged(state.statuses);
      } catch (_) { /* keep optimistic order rather than blank the board */ }
    }
    renderKanban();
  };

  document.addEventListener('mousedown', onColMouseDown);
  document.addEventListener('mousemove', onColMouseMove);
  document.addEventListener('mouseup', onColMouseUp);
}

function cleanupColumnDrag() {
  stopEdgeScroll();
  if (state.colDrag) {
    if (state.colDrag.clone) state.colDrag.clone.remove();
    if (state.colDrag.placeholder) state.colDrag.placeholder.remove();
    if (state.colDrag.el) state.colDrag.el.classList.remove('kanban-column-hidden');
    state.colDrag = null;
  }
  if (onColMouseDown) document.removeEventListener('mousedown', onColMouseDown);
  if (onColMouseMove) document.removeEventListener('mousemove', onColMouseMove);
  if (onColMouseUp) document.removeEventListener('mouseup', onColMouseUp);
}

// ── Grab-to-pan (TPT113) ──
// Mouse-down on free board space (not a card, not a native control, and — in Edit Board
// mode — not inside a column, which belongs to the column drag there) pans the board
// horizontally by tracking clientX against scrollLeft. Same house pointer-drag idiom as
// the two drags above, minus the clone/placeholder machinery: panning has no drop step,
// just a live scrollLeft write.

let onPanMouseDown, onPanMouseMove, onPanMouseUp;

function bindBoardPan() {
  const board = document.getElementById('kanban-board');
  if (board) board.classList.add('kanban-board-pannable');

  onPanMouseDown = (e) => {
    if (e.button !== 0) return;
    const board = e.target.closest('.kanban-board');
    if (!board) return;
    if (state.drag || state.colDrag) return; // a card/column drag already claimed this gesture
    if (!isFreeBoardSpace(e.target)) return;
    if (e.target.closest('.kanban-column-drag-handle')) return;
    // Edit Board mode: free space *inside* a column starts a column reorder drag instead —
    // pan only claims space outside every column (board gutter, right of the last column).
    if (state.editBoardMode && e.target.closest('.kanban-column')) return;

    state.pan = {
      board,
      startX: e.clientX,
      startScrollLeft: board.scrollLeft,
      moved: false,
    };
    e.preventDefault();
  };

  onPanMouseMove = (e) => {
    const p = state.pan;
    if (!p) return;
    const dx = e.clientX - p.startX;
    if (!p.moved) {
      if (Math.abs(dx) < DRAG_THRESHOLD) return;
      p.moved = true;
      p.board.classList.add('kanban-board-panning');
    }
    p.board.scrollLeft = p.startScrollLeft - dx;
  };

  onPanMouseUp = () => {
    const p = state.pan;
    if (!p) return;
    p.board.classList.remove('kanban-board-panning');
    state.pan = null;
  };

  document.addEventListener('mousedown', onPanMouseDown);
  document.addEventListener('mousemove', onPanMouseMove);
  document.addEventListener('mouseup', onPanMouseUp);
}

function cleanupBoardPan() {
  if (state.pan) {
    state.pan.board.classList.remove('kanban-board-panning');
    state.pan = null;
  }
  const board = document.getElementById('kanban-board');
  if (board) board.classList.remove('kanban-board-pannable');
  if (onPanMouseDown) document.removeEventListener('mousedown', onPanMouseDown);
  if (onPanMouseMove) document.removeEventListener('mousemove', onPanMouseMove);
  if (onPanMouseUp) document.removeEventListener('mouseup', onPanMouseUp);
}

// ── Sub-view render (used by board container) ──

export function renderKanbanView(container, tasks, projectId, members, statuses, opts = {}) {
  resetState();
  state.projectId = projectId;
  state.tasks = tasks;
  state.members = members || [];
  state.statuses = statuses || [];
  state.editBoardMode = !!opts.editBoardMode;
  state.onStatusesChanged = opts.onStatusesChanged || null;
  state.onDataChanged = opts.onDataChanged || null;

  container.innerHTML = '<div id="kanban-board" class="kanban-board"></div>';
  renderKanban();
  // kanban-board--edit (TPT113) styles the whole column as a drag target in Edit Board mode.
  document.getElementById('kanban-board')?.classList.toggle('kanban-board--edit', state.editBoardMode);
  bindDragAndDrop();
  if (state.editBoardMode) bindColumnDrag();
  bindBoardPan();
  bindQuickCreate(container, {
    projectId,
    allTasks: tasks,
    // Reload through the owner instead of a local renderKanban(): quick-create pushes the
    // new task into `tasks` (the possibly-filtered array), which would render it even when
    // it doesn't match the active filter, and board.js's own state.tasks would never learn
    // about it (C1261).
    onCreated: () => reloadTasks(),
    getContext: (triggerEl) => {
      const colBody = triggerEl.closest('.kanban-column-body');
      return { status: colBody?.dataset.status || roleStatus(state.statuses, 'start')?.name || 'pending' };
    },
  });
  return () => {
    removeQuickCreate();
    removeAssigneePicker();
    closeTaskDetail();
    cleanupDrag();
    cleanupColumnDrag();
    cleanupBoardPan();
    resetState();
  };
}

// ── Page entry point (legacy) ──

export async function kanbanPage(params) {
  if (!(await checkAuth())) return;
  resetState();
  state.projectId = params.id;

  initLayout(getUser());
  updateLayoutContext({ projectId: params.id, projectName: null, activeView: 'kanban', viewLabel: 'Kanban' });

  const content = getContentEl();
  content.innerHTML = `
    <div class="page-header">
      <h2>Kanban</h2>
    </div>
    <div id="kanban-board" class="kanban-board"><div class="empty"><span class="spinner"></span></div></div>`;

  try {
    const [tasksRes, membersRes, statuses] = await Promise.all([
      api.get('/projects/' + params.id + '/tasks'),
      api.get('/projects/' + params.id + '/members'),
      loadStatuses(params.id),
    ]);
    state.tasks = tasksRes.tasks || [];
    state.members = membersRes.members || [];
    state.statuses = statuses || [];
    renderKanban();
    bindDragAndDrop();
    bindBoardPan();
    bindQuickCreate(content, {
      projectId: params.id,
      allTasks: state.tasks,
      onCreated: () => renderKanban(),
      getContext: (triggerEl) => {
        const colBody = triggerEl.closest('.kanban-column-body');
        return { status: colBody?.dataset.status || roleStatus(state.statuses, 'start')?.name || 'pending' };
      },
    });
  } catch (err) {
    document.getElementById('kanban-board').innerHTML =
      `<div class="empty text-muted">Failed to load tasks</div>`;
  }

  return () => {
    removeQuickCreate();
    removeAssigneePicker();
    closeTaskDetail();
    cleanupDrag();
    cleanupColumnDrag();
    cleanupBoardPan();
    resetState();
  };
}
